import {
  administrative,
  contact,
  declareFieldMeanings,
  identity,
  means,
} from '../../../src/issues/field-meanings';

/**
 * What each detail on a DBI permit, complaint or notice page means to an owner.
 * The labels are the ones the Permit and Complaint Tracking System and the
 * PermitSF feed publish (captured from live pages on 2026-10-23); contact,
 * routing and identity details are never news, deadlines and requirements are.
 */
const DEADLINE = 'moves the deadline for the required work';
const REQUIREMENT = 'changes what the notice requires of you';
const OWE = 'changes what you may owe';
const MAIL = 'changes where the city sends notices about this record';
export const fieldMeanings = declareFieldMeanings({
  sources: ['dbi'],
  declarations: [
    contact(
      'Inspector D Phone',
      'Inspector Firstname',
      'Inspector Lastname',
      'Inspector Mi',
      'Inspector',
      'Inspected By',
      'Checked By',
      'Reinsp Name',
      'Reinsp Ph Num',
      'Recvd By',
      'Received By',
      'Updated By',
      'Sitecontactlastname',
      'Contact Phone',
      'Contact Information',
      'Contractor Addr',
      'Lic No',
      'License Number',
      'Phone',
    ),
    administrative(
      'Assigned Div',
      'Assigned to Division',
      'Division',
      'Division Id',
      'Div',
      'Src',
      'Source',
      'Complaint Source',
      'Form No',
      'Form Number',
      'Record type',
      'Building Type',
      'Bldg Use',
      'Building Use',
      'Use Type',
      'Occupancy Code',
      'Occupancy Desc',
      'Notice type',
      'Complainant',
      'Owner/Agent',
      'Msg',
      'No Addenda Records',
      'Permit Issued',
      'Number of Plan Sets',
      'Inspection Code',
      'Activity Code',
      'Appointment Code',
      'Station',
      'Role',
      'District',
      'Record ID',
      'Data as of',
      'Report date',
      'Title',
      'Instructions',
    ),
    identity(
      'Complaint number',
      'Notice',
      'No',
      'Permit number',
      'Permit No',
      'Permit (Application) Number',
      'Application Number',
      'App No',
      'Block',
      'Lot',
      'Parcel number',
      'Address',
      'Location',
      'Street name',
      'Street number',
      'Street suffix',
      'Unit',
      'Addenda No.',
      'Addendum',
      'Plans Revision No.',
      'Rev#',
    ),
    means('deadline', 'the time allowed to correct the violation', ['Correctvioldays'], {
      matters: DEADLINE,
      unit: 'days',
    }),
    means('deadline', 'the time allowed to complete the work', ['Completeworkdays'], {
      matters: DEADLINE,
      unit: 'days',
    }),
    means('deadline', 'the time allowed to obtain a permit', ['Obtainpermitdays'], {
      matters: DEADLINE,
      unit: 'days',
    }),
    means(
      'deadline',
      'the time allowed to file a building permit application',
      ['Filebldgpermitdays'],
      { matters: DEADLINE, unit: 'days' },
    ),
    means('deadline', 'the time allowed to comply', ['Days Comp'], {
      matters: DEADLINE,
      unit: 'days',
    }),
    means('deadline', 'the expiration date', ['Expiration date'], {
      matters: 'changes how long this permit stays valid',
    }),
    means('deadline', 'the failure-to-comply date', ['Failuredate'], {
      matters: 'moves the date the city counts the notice as unmet',
    }),
    means('appointment', 'the reinspection date', ['Reinsp Date'], {
      matters: "moves the inspector's visit",
    }),
    means(
      'appointment',
      'the inspection appointment',
      ['Appointment Date', 'Appointment AM/PM', 'Appointment Type', 'Time Slots'],
      { matters: 'moves the inspection appointment' },
    ),
    means('status', 'the status', [
      'Status',
      'Current Disposition',
      'Stage',
      'Inspection Status',
      'Review Result',
    ]),
    means('description', 'how it describes the violation', ['Codeviolationdesc'], {
      matters: 'restated what it says is wrong',
      means:
        "This is the city's description of the violation. A change means the city restated what it says is wrong.",
    }),
    means(
      'description',
      'its comment on this record',
      ['Comments', 'Comment', 'Remarks', 'Hold Description'],
      {
        matters: 'added or changed a note',
        means:
          "This is the city's own note on the record. A change means the city added or changed what it says.",
      },
    ),
    means('description', 'its failure-to-comply note', ['Failurecommentdesc'], {
      matters: 'changed the consequence it states',
      means:
        'This is what the city says will happen if the notice is not met. A change means the consequence it states changed.',
    }),
    means(
      'description',
      'the description',
      [
        'Description',
        'Desc',
        'Item description',
        'Activity Code Descr.',
        'Addenda Desc',
        'Inspection Description',
      ],
      {
        matters: 'reworded it',
        means: "This is the record's description. A change means the city reworded it.",
      },
    ),
    means('requirement', 'the requirement to stop all work', ['Stopallwork'], {
      matters: REQUIREMENT,
    }),
    means('requirement', 'the requirement to obtain a permit', ['Obtainpermit'], {
      matters: REQUIREMENT,
    }),
    means('requirement', 'the requirement to correct the violation', ['Correctviolation'], {
      matters: REQUIREMENT,
    }),
    means(
      'requirement',
      'the requirement to file a building permit application',
      ['Filebldgpermitapp'],
      {
        matters: REQUIREMENT,
      },
    ),
    means(
      'requirement',
      'the requirement to submit building permit plans',
      ['Filebldgpermitplans'],
      {
        matters: REQUIREMENT,
      },
    ),
    means('requirement', 'the unsafe building designation', ['Unsafebldg'], {
      matters: REQUIREMENT,
    }),
    means('requirement', 'the no-penalty designation', ['Nopenalty'], {
      matters: 'changes whether a penalty applies',
    }),
    means('requirement', 'the no-permit-required designation', ['Nopermitreq'], {
      matters: 'changes whether a permit is required',
    }),
    means('requirement', 'the finding of work without a permit', ['Workwopermit'], {
      matters: REQUIREMENT,
    }),
    means('amount', 'the nine-times permit fee', ['Ninexfee'], { matters: OWE }),
    means('amount', 'the value of work without a permit', ['Workwopermitvalue'], { matters: OWE }),
    means('amount', 'the other fee stated on the notice', ['Otherfeedesc'], { matters: OWE }),
    means('amount', 'the stated cost of the work', ['Cost'], {
      matters: 'changes the value the permit declares',
    }),
    means(
      'mailing',
      'the owner mailing address on this notice',
      ['Owner Address1', 'Owner Address2', 'Owner Address3', 'Owner Address4', 'Owner Zip'],
      { matters: MAIL },
    ),
    means('mailing', 'the owner or agent named on this notice', ['Owner or agent'], {
      matters: MAIL,
    }),
    means('detail', 'the contractor named on this permit', [
      'Company Name',
      'Contractor Name',
      'Name',
      'Firm Name',
      'Agent Name',
    ]),
    means('detail', 'the latest document on this permit', ['Last document'], {
      matters: 'means the city issued or recorded a new document',
    }),
    means('date', 'the date of the latest document on this permit', ['Last document date']),
    means('date', 'the issue date', ['Issued date']),
    means('date', 'the filing date', ['Submitted date']),
    means('date', 'the record date', ['Date', 'date']),
    means('date', 'the date received', ['Received']),
    means('date', 'the completion date', ['Completed Date']),
    means('date', 'the activity date', ['Activity Date', 'Action Date']),
    means('date', 'a date on this record', [
      'Arrive',
      'Start',
      'Finish',
      'From',
      'To',
      'In Hold',
      'Out Hold',
    ]),
    means('detail', 'the number of special inspections', ['Total Special Inspections']),
    means('detail', 'the count of completed items', ['Completed']),
    means('detail', 'the priority the city assigns', ['Priority']),
    means('detail', 'the entry type', ['Type']),
  ],
});
